import {isFeatureEnabled} from '@github-ui/feature-flags'
import {VALUES} from '@github-ui/commenting/Values'
import {getAutomatedReviewer} from '@github-ui/hovercards/automated-reviewers'
import type {
  GetHovercardAttributesForTypeFn,
  HovercardAttributesResult,
} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Label, Link} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {UserType} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {isCopilotReviewer} from '../../overview/reviewers/copilot-reviewer'
import styles from './TimelineAuthor.module.css'

type Props = {
  login?: string | null
  avatarUrl?: string | null
  slug?: string | null
  userType?: UserType
}

export function TimelineAuthor({login, avatarUrl, slug, userType}: Props) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const author = {
    login: login || VALUES.ghostUser.login,
    //if we have a login, but no avatarUrl, we leave it undefined so it doesn't show a ghost
    avatarUrl: avatarUrl || (login ? '' : VALUES.ghostUser.avatarUrl),
  }
  const automatedReviewer = isFeatureEnabled('automated_review_comment_reviewer_identity')
    ? getAutomatedReviewer(slug ?? undefined)
    : undefined
  const hasUserType = userType !== undefined && userType !== UserType.UNSPECIFIED
  const isAI = hasUserType
    ? userType === UserType.AGENT
    : isCopilotReviewer({displayLogin: author.login, slug: slug ?? ''})
  const profileUrl = slug ? `/apps/${slug}` : `/${isAI ? 'apps/' : ''}${author.login}`
  // Prefer the real `userType`; fall back to the slug/login heuristic for timeline
  // surfaces (e.g. catch-up snapshot) whose user payload doesn't carry it yet.
  const showTypeBadge = hasUserType ? userType !== UserType.HUMAN : isAI || !!slug
  // The type badge already says "Bot"/"AI", so the `[bot]` login suffix is redundant in the display name.
  const displayLogin = showTypeBadge ? author.login.replace(/\[bot\]$/i, '') : author.login
  const hovercardAttributes = getAuthorHovercardAttributes({
    getHovercardAttributesForType,
    isAI,
    automatedReviewerSlug: isFeatureEnabled('automated_review_comment_reviewer_hovercard')
      ? automatedReviewer?.slug
      : undefined,
    login: author.login,
    slug,
  })

  return (
    // margin right here to make sure timeline text lines up correctly when no author
    <span className="mr-1">
      <Link
        href={profileUrl}
        {...hovercardAttributes}
        aria-label={`${displayLogin}'s profile`}
        className={`${styles.author} fgColor-default`}
      >
        {author.avatarUrl && <GitHubAvatar className={styles.avatar} src={author.avatarUrl} size={16} />}
        <strong className="fgColor-default ml-1">{displayLogin}</strong>
      </Link>
      {showTypeBadge && (
        <Label variant="secondary" className="ml-1">
          {isAI ? 'AI' : 'Bot'}
        </Label>
      )}
    </span>
  )
}

// Precedence: Copilot > known automated reviewer > unrecognized app (no hovercard) > regular user.
function getAuthorHovercardAttributes({
  getHovercardAttributesForType,
  isAI,
  automatedReviewerSlug,
  login,
  slug,
}: {
  getHovercardAttributesForType: GetHovercardAttributesForTypeFn
  isAI: boolean
  automatedReviewerSlug: string | undefined
  login: string
  slug?: string | null
}): HovercardAttributesResult {
  if (isAI) return getHovercardAttributesForType('copilot', {botSlug: slug || login})
  if (automatedReviewerSlug)
    return getHovercardAttributesForType('automated_reviewer', {botSlug: automatedReviewerSlug})
  if (slug) return {}
  return getHovercardAttributesForType('user', {login})
}
